import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  Home, Building, MapPin, Bed, Bath, Calendar, User, Mail, Phone,
  ArrowLeft, ArrowRight, Check, PoundSterling, Clock, Key, TrendingUp
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export const Valuation = () => {
  const { isArabic } = useLanguage();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const totalSteps = 4;

  const [formData, setFormData] = useState({
    // Step 1: Intent
    intent: '', // 'sell' or 'rent'
    timeframe: '', // 'asap', '1-3months', '3-6months', 'notSure'
    
    // Step 2: Property Details
    propertyType: '',
    bedrooms: '',
    bathrooms: '',
    propertyCondition: '',
    
    // Step 3: Location
    address: '',
    postcode: '',
    city: '',
    
    // Step 4: Contact
    name: '',
    email: '',
    phone: '',
    preferredContact: '',
    additionalInfo: ''
  });

  const propertyTypes = [
    { id: 'detached', label: isArabic ? 'منزل منفصل' : 'Detached House', icon: Home },
    { id: 'semi-detached', label: isArabic ? 'شبه منفصل' : 'Semi-Detached', icon: Home },
    { id: 'terraced', label: isArabic ? 'متلاصق' : 'Terraced', icon: Home },
    { id: 'flat', label: isArabic ? 'شقة' : 'Flat / Apartment', icon: Building },
    { id: 'bungalow', label: isArabic ? 'بنغالو' : 'Bungalow', icon: Home },
    { id: 'maisonette', label: isArabic ? 'ميزونيت' : 'Maisonette', icon: Building },
  ];

  const timeframes = [
    { id: 'asap', label: isArabic ? 'في أقرب وقت ممكن' : 'As soon as possible', icon: Clock },
    { id: '1-3months', label: isArabic ? '1-3 أشهر' : '1-3 months', icon: Calendar },
    { id: '3-6months', label: isArabic ? '3-6 أشهر' : '3-6 months', icon: Calendar },
    { id: 'notSure', label: isArabic ? 'غير متأكد بعد' : 'Not sure yet', icon: Clock },
  ];

  const conditions = [
    { id: 'excellent', label: isArabic ? 'ممتاز' : 'Excellent - Recently renovated' },
    { id: 'good', label: isArabic ? 'جيد' : 'Good - Well maintained' },
    { id: 'fair', label: isArabic ? 'مقبول' : 'Fair - Some updates needed' },
    { id: 'poor', label: isArabic ? 'يحتاج تجديد' : 'Needs renovation' },
  ];

  const handleSubmit = () => {
    // Mock submission
    setIsSubmitted(true);
  };

  const nextStep = () => setCurrentStep(prev => Math.min(prev + 1, totalSteps));
  const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[1, 2, 3, 4].map((step) => (
        <React.Fragment key={step}>
          <div 
            className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
              step < currentStep 
                ? 'bg-green-500 text-white' 
                : step === currentStep 
                  ? 'bg-[#FF8C42] text-white' 
                  : 'bg-gray-200 text-gray-500'
            }`}
          >
            {step < currentStep ? <Check className="w-5 h-5" /> : step}
          </div>
          {step < 4 && (
            <div className={`w-16 h-1 mx-1 ${step < currentStep ? 'bg-green-500' : 'bg-gray-200'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-8">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-[#404040] mb-2">
          {isArabic ? 'ما هدفك؟' : "What's your goal?"}
        </h2>
        <p className="text-gray-600">
          {isArabic ? 'اختر ما تريد القيام به بعقارك' : 'Choose what you want to do with your property'}
        </p>
      </div>

      {/* Intent */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <button
          type="button"
          onClick={() => setFormData({...formData, intent: 'sell'})}
          className={`p-8 rounded-xl border-2 transition-all ${
            formData.intent === 'sell'
              ? 'border-[#FF8C42] bg-[#FF8C42]/5'
              : 'border-gray-200 hover:border-[#FF8C42]/50'
          }`}
        >
          <PoundSterling className={`w-16 h-16 mx-auto mb-4 ${formData.intent === 'sell' ? 'text-[#FF8C42]' : 'text-gray-400'}`} />
          <p className="text-xl font-semibold text-[#404040]">{isArabic ? 'أريد البيع' : 'I want to sell'}</p>
          <p className="text-gray-500 mt-2">{isArabic ? 'احصل على تقييم مجاني لسعر البيع' : 'Get a free sale price valuation'}</p>
        </button>
        
        <button
          type="button"
          onClick={() => setFormData({...formData, intent: 'rent'})}
          className={`p-8 rounded-xl border-2 transition-all ${
            formData.intent === 'rent'
              ? 'border-[#FF8C42] bg-[#FF8C42]/5'
              : 'border-gray-200 hover:border-[#FF8C42]/50'
          }`}
        >
          <Key className={`w-16 h-16 mx-auto mb-4 ${formData.intent === 'rent' ? 'text-[#FF8C42]' : 'text-gray-400'}`} />
          <p className="text-xl font-semibold text-[#404040]">{isArabic ? 'أريد التأجير' : 'I want to rent out'}</p>
          <p className="text-gray-500 mt-2">{isArabic ? 'احصل على تقييم مجاني للإيجار' : 'Get a free rental valuation'}</p>
        </button>
      </div>

      {/* Timeframe */}
      {formData.intent && (
        <div className="fade-in">
          <Label className="text-lg font-semibold text-[#404040] mb-4 block">
            {isArabic ? 'متى تخطط للبدء؟' : 'When are you looking to start?'}
          </Label>
          <div className="grid grid-cols-2 gap-3">
            {timeframes.map((tf) => (
              <button
                key={tf.id}
                type="button"
                onClick={() => setFormData({...formData, timeframe: tf.id})}
                className={`p-4 rounded-xl border-2 transition-all flex items-center gap-3 ${
                  formData.timeframe === tf.id
                    ? 'border-[#FF8C42] bg-[#FF8C42]/5'
                    : 'border-gray-200 hover:border-[#FF8C42]/50'
                }`}
              >
                <tf.icon className={`w-6 h-6 ${formData.timeframe === tf.id ? 'text-[#FF8C42]' : 'text-gray-400'}`} />
                <span className="font-medium text-[#404040]">{tf.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-[#404040] mb-2">
          {isArabic ? 'أخبرنا عن عقارك' : 'Tell us about your property'}
        </h2>
        <p className="text-gray-600">
          {isArabic ? 'هذا يساعدنا على تقديم تقييم دقيق' : 'This helps us provide an accurate valuation'}
        </p>
      </div>

      {/* Property Type */}
      <div>
        <Label className="text-lg font-semibold text-[#404040] mb-4 block">
          {isArabic ? 'نوع العقار' : 'Property Type'}
        </Label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {propertyTypes.map((type) => (
            <button
              key={type.id}
              type="button"
              onClick={() => setFormData({...formData, propertyType: type.id})}
              className={`p-4 rounded-xl border-2 transition-all text-center ${
                formData.propertyType === type.id
                  ? 'border-[#FF8C42] bg-[#FF8C42]/5'
                  : 'border-gray-200 hover:border-[#FF8C42]/50'
              }`}
            >
              <type.icon className={`w-8 h-8 mx-auto mb-2 ${formData.propertyType === type.id ? 'text-[#FF8C42]' : 'text-gray-400'}`} />
              <p className="font-medium text-[#404040] text-sm">{type.label}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Bedrooms & Bathrooms */}
      <div className="grid grid-cols-2 gap-6">
        <div>
          <Label className="flex items-center gap-2 mb-3">
            <Bed className="w-5 h-5 text-[#FF8C42]" />
            {isArabic ? 'غرف النوم' : 'Bedrooms'}
          </Label>
          <div className="flex gap-2">
            {['1', '2', '3', '4', '5+'].map((num) => (
              <button
                key={num}
                type="button"
                onClick={() => setFormData({...formData, bedrooms: num})}
                className={`flex-1 py-3 rounded-lg border-2 transition-all font-semibold ${
                  formData.bedrooms === num
                    ? 'border-[#FF8C42] bg-[#FF8C42] text-white'
                    : 'border-gray-200 text-gray-600 hover:border-[#FF8C42]/50'
                }`}
              >
                {num}
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label className="flex items-center gap-2 mb-3">
            <Bath className="w-5 h-5 text-[#FF8C42]" />
            {isArabic ? 'الحمامات' : 'Bathrooms'}
          </Label>
          <div className="flex gap-2">
            {['1', '2', '3', '4+'].map((num) => (
              <button
                key={num}
                type="button"
                onClick={() => setFormData({...formData, bathrooms: num})}
                className={`flex-1 py-3 rounded-lg border-2 transition-all font-semibold ${
                  formData.bathrooms === num
                    ? 'border-[#FF8C42] bg-[#FF8C42] text-white'
                    : 'border-gray-200 text-gray-600 hover:border-[#FF8C42]/50'
                }`}
              >
                {num}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Property Condition */}
      <div>
        <Label className="text-lg font-semibold text-[#404040] mb-4 block">
          {isArabic ? 'حالة العقار' : 'Property Condition'}
        </Label>
        <div className="space-y-2">
          {conditions.map((condition) => (
            <button
              key={condition.id}
              type="button"
              onClick={() => setFormData({...formData, propertyCondition: condition.id})}
              className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                formData.propertyCondition === condition.id
                  ? 'border-[#FF8C42] bg-[#FF8C42]/5'
                  : 'border-gray-200 hover:border-[#FF8C42]/50'
              }`}
            >
              <span className="font-medium text-[#404040]">{condition.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-[#404040] mb-2">
          {isArabic ? 'أين يقع عقارك؟' : 'Where is your property located?'}
        </h2>
        <p className="text-gray-600">
          {isArabic ? 'أدخل عنوان العقار' : 'Enter your property address'}
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <Label htmlFor="address">{isArabic ? 'العنوان' : 'Street Address'}</Label>
          <div className="relative mt-1">
            <MapPin className={`absolute top-3 ${isArabic ? 'right-3' : 'left-3'} w-5 h-5 text-gray-400`} />
            <Input
              id="address"
              placeholder={isArabic ? 'مثال: 25 شارع الرئيسي' : 'e.g., 25 Main Street'}
              value={formData.address}
              onChange={(e) => setFormData({...formData, address: e.target.value})}
              className={`${isArabic ? 'pr-10' : 'pl-10'} py-6`}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="city">{isArabic ? 'المدينة' : 'City / Town'}</Label>
            <Input
              id="city"
              placeholder={isArabic ? 'مثال: هارو' : 'e.g., Harrow'}
              value={formData.city}
              onChange={(e) => setFormData({...formData, city: e.target.value})}
              className="mt-1 py-6"
            />
          </div>
          <div>
            <Label htmlFor="postcode">{isArabic ? 'الرمز البريدي' : 'Postcode'}</Label>
            <Input
              id="postcode"
              placeholder={isArabic ? 'مثال: HA3 5AH' : 'e.g., HA3 5AH'}
              value={formData.postcode}
              onChange={(e) => setFormData({...formData, postcode: e.target.value})}
              className="mt-1 py-6"
            />
          </div>
        </div>
      </div>

      {/* Local Market Info */}
      <Card className="bg-[#FF8C42]/5 border-[#FF8C42]/20">
        <CardContent className="p-6">
          <div className={`flex items-start gap-4 ${isArabic ? 'flex-row-reverse' : ''}`}>
            <TrendingUp className="w-8 h-8 text-[#FF8C42] flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-[#404040]">
                {isArabic ? 'هارو - سوق نشط' : 'Harrow - Active Market'}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {isArabic 
                  ? 'متوسط وقت البيع: 45 يوم | متوسط السعر: £485,000'
                  : 'Average time to sell: 45 days | Average price: £485,000'
                }
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-[#404040] mb-2">
          {isArabic ? 'معلومات الاتصال' : 'Your Contact Details'}
        </h2>
        <p className="text-gray-600">
          {isArabic ? 'سنتواصل معك خلال 24 ساعة' : "We'll contact you within 24 hours"}
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <Label htmlFor="name">{isArabic ? 'الاسم الكامل' : 'Full Name'}</Label>
          <div className="relative mt-1">
            <User className={`absolute top-3 ${isArabic ? 'right-3' : 'left-3'} w-5 h-5 text-gray-400`} />
            <Input
              id="name"
              placeholder={isArabic ? 'أدخل اسمك' : 'Enter your name'}
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className={`${isArabic ? 'pr-10' : 'pl-10'} py-6`}
            />
          </div>
        </div>

        <div>
          <Label htmlFor="email">{isArabic ? 'البريد الإلكتروني' : 'Email Address'}</Label>
          <div className="relative mt-1">
            <Mail className={`absolute top-3 ${isArabic ? 'right-3' : 'left-3'} w-5 h-5 text-gray-400`} />
            <Input
              id="email"
              type="email"
              placeholder={isArabic ? 'أدخل بريدك الإلكتروني' : 'Enter your email'}
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              className={`${isArabic ? 'pr-10' : 'pl-10'} py-6`}
            />
          </div>
        </div>

        <div>
          <Label htmlFor="phone">{isArabic ? 'رقم الهاتف' : 'Phone Number'}</Label>
          <div className="relative mt-1">
            <Phone className={`absolute top-3 ${isArabic ? 'right-3' : 'left-3'} w-5 h-5 text-gray-400`} />
            <Input
              id="phone"
              type="tel"
              placeholder={isArabic ? 'أدخل رقم هاتفك' : 'Enter your phone number'}
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              className={`${isArabic ? 'pr-10' : 'pl-10'} py-6`}
            />
          </div>
        </div>

        <div>
          <Label>{isArabic ? 'طريقة الاتصال المفضلة' : 'Preferred Contact Method'}</Label>
          <div className="grid grid-cols-3 gap-3 mt-2">
            {['phone', 'email', 'whatsapp'].map((method) => (
              <button
                key={method}
                type="button"
                onClick={() => setFormData({...formData, preferredContact: method})}
                className={`p-3 rounded-lg border-2 transition-all ${
                  formData.preferredContact === method
                    ? 'border-[#FF8C42] bg-[#FF8C42]/5'
                    : 'border-gray-200 hover:border-[#FF8C42]/50'
                }`}
              >
                <span className="font-medium text-[#404040] capitalize">
                  {method === 'phone' ? (isArabic ? 'هاتف' : 'Phone') :
                   method === 'email' ? (isArabic ? 'بريد' : 'Email') :
                   'WhatsApp'}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label htmlFor="additionalInfo">{isArabic ? 'معلومات إضافية (اختياري)' : 'Additional Information (optional)'}</Label>
          <Textarea
            id="additionalInfo"
            placeholder={isArabic 
              ? 'أخبرنا بأي شيء آخر عن عقارك...'
              : 'Tell us anything else about your property...'
            }
            value={formData.additionalInfo}
            onChange={(e) => setFormData({...formData, additionalInfo: e.target.value})}
            className="mt-1 min-h-[100px]"
          />
        </div>
      </div>
    </div>
  );

  const renderSuccess = () => (
    <div className="text-center py-12">
      <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
        <Check className="w-12 h-12 text-green-500" />
      </div>
      <h2 className="text-3xl font-bold text-[#404040] mb-4">
        {isArabic ? 'شكراً لك!' : 'Thank You!'}
      </h2>
      <p className="text-lg text-gray-600 mb-2">
        {isArabic 
          ? 'تم استلام طلب التقييم الخاص بك'
          : 'Your valuation request has been received'
        }
      </p>
      <p className="text-gray-500 mb-8">
        {isArabic 
          ? 'سيتواصل معك أحد خبرائنا خلال 24 ساعة'
          : 'One of our experts will contact you within 24 hours'
        }
      </p>
      <div className="bg-[#FF8C42]/5 rounded-xl p-6 max-w-md mx-auto mb-8">
        <h3 className="font-semibold text-[#404040] mb-3">
          {isArabic ? 'ماذا يحدث بعد ذلك؟' : 'What happens next?'}
        </h3>
        <ul className={`text-sm text-gray-600 space-y-2 ${isArabic ? 'text-right' : 'text-left'}`}>
          <li className={`flex items-center gap-2 ${isArabic ? 'flex-row-reverse' : ''}`}>
            <Check className="w-4 h-4 text-green-500" />
            {isArabic ? 'سنراجع تفاصيل عقارك' : "We'll review your property details"}
          </li>
          <li className={`flex items-center gap-2 ${isArabic ? 'flex-row-reverse' : ''}`}>
            <Check className="w-4 h-4 text-green-500" />
            {isArabic ? 'سنتصل بك لترتيب زيارة' : "We'll call to arrange a visit"}
          </li>
          <li className={`flex items-center gap-2 ${isArabic ? 'flex-row-reverse' : ''}`}>
            <Check className="w-4 h-4 text-green-500" />
            {isArabic ? 'ستحصل على تقييم مفصل' : "You'll receive a detailed valuation"}
          </li>
        </ul>
      </div>
      <Button 
        onClick={() => window.location.href = '/'}
        className="bg-[#FF8C42] hover:bg-[#FF6B35] text-white"
      >
        {isArabic ? 'العودة للرئيسية' : 'Back to Home'}
      </Button>
    </div>
  );

  if (isSubmitted) {
    return (
      <div className="min-h-screen pt-24 pb-16 bg-gradient-to-br from-gray-50 to-gray-100" dir={isArabic ? 'rtl' : 'ltr'}>
        <div className="max-w-2xl mx-auto px-4">
          <Card className="border-none shadow-xl">
            <CardContent className="p-8">
              {renderSuccess()}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 bg-gradient-to-br from-gray-50 to-gray-100" dir={isArabic ? 'rtl' : 'ltr'}>
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-[#404040]">
            {isArabic ? 'احصل على تقييم مجاني' : 'Get Your Free Valuation'}
          </h1>
          <p className="text-gray-600 mt-2">
            {isArabic 
              ? 'اكتشف قيمة عقارك في دقائق'
              : 'Discover your property value in minutes'
            }
          </p>
        </div>

        {/* Step Indicator */}
        {renderStepIndicator()}

        {/* Form Card */}
        <Card className="border-none shadow-xl">
          <CardContent className="p-8">
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
            {currentStep === 4 && renderStep4()}

            {/* Navigation Buttons */}
            <div className={`flex justify-between mt-8 pt-6 border-t ${isArabic ? 'flex-row-reverse' : ''}`}>
              {currentStep > 1 ? (
                <Button variant="outline" onClick={prevStep} className="px-6">
                  <ArrowLeft className={`w-5 h-5 ${isArabic ? 'ml-2 rotate-180' : 'mr-2'}`} />
                  {isArabic ? 'السابق' : 'Previous'}
                </Button>
              ) : (
                <div></div>
              )}

              {currentStep < totalSteps ? (
                <Button 
                  onClick={nextStep} 
                  className="bg-[#FF8C42] hover:bg-[#FF6B35] text-white px-6"
                  disabled={
                    (currentStep === 1 && !formData.intent) ||
                    (currentStep === 2 && !formData.propertyType)
                  }
                >
                  {isArabic ? 'التالي' : 'Next'}
                  <ArrowRight className={`w-5 h-5 ${isArabic ? 'mr-2 rotate-180' : 'ml-2'}`} />
                </Button>
              ) : (
                <Button 
                  onClick={handleSubmit} 
                  className="bg-green-500 hover:bg-green-600 text-white px-8"
                  disabled={!formData.name || !formData.email || !formData.phone}
                >
                  <Check className={`w-5 h-5 ${isArabic ? 'ml-2' : 'mr-2'}`} />
                  {isArabic ? 'إرسال الطلب' : 'Submit Request'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Trust Badges */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500 mb-4">
            {isArabic ? 'موثوق به من قبل أكثر من 500 عميل' : 'Trusted by 500+ clients'}
          </p>
          <div className={`flex justify-center gap-8 ${isArabic ? 'flex-row-reverse' : ''}`}>
            <div className="text-center">
              <p className="text-2xl font-bold text-[#FF8C42]">97%</p>
              <p className="text-xs text-gray-500">{isArabic ? 'سعر الطلب المحقق' : 'Asking Price Achieved'}</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-[#FF8C42]">24h</p>
              <p className="text-xs text-gray-500">{isArabic ? 'وقت الاستجابة' : 'Response Time'}</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-[#FF8C42]">15+</p>
              <p className="text-xs text-gray-500">{isArabic ? 'سنوات الخبرة' : 'Years Experience'}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
